import { z } from 'zod'

import { occasionType } from './product.js'

/**
 * admin 리포트 (BIZ-123). 전사 집계 — 운영자 전용.
 * 탭 3개: 매출(sales) · 정산(settlement) · 운영(ops).
 */

// ── 공통 ────────────────────────────────────────────────

/** 추이 버킷 단위. 일/주/월 — 주는 ISO 주 시작(월요일) 날짜로 표기한다. */
export const reportBucket = z.enum(['day', 'week', 'month'])
export type ReportBucket = z.infer<typeof reportBucket>

export const reportRangeQuery = z.object({
  /** 시작일(YYYY-MM-DD, KST, 포함) */
  from: z.string().date().optional(),
  /** 종료일(YYYY-MM-DD, KST, 포함) */
  to: z.string().date().optional(),
})

// ── A. 매출 ─────────────────────────────────────────────

export const salesReportQuery = reportRangeQuery.extend({
  bucket: reportBucket.optional().default('day'),
})
export type SalesReportQuery = z.infer<typeof salesReportQuery>

export const salesReportResponse = z.object({
  totals: z.object({
    orders: z.number().int(), // 접수 주문 수(취소 제외)
    revenue: z.number().int(), // 판매액 합
    delivered: z.number().int(), // 배송 완료 건수
    /** 마진 = 판매액 − 공급액. 공급 스냅샷이 잡힌 주문만 합산한다. (포인트 지급 비용은 빼기 전 총마진) */
    margin: z.number().int(),
    /** 마진 계산에 포함된(공급 스냅샷 있는) 주문 수 — 커버리지 표기용 */
    marginOrders: z.number().int(),
    /** 포인트 지급(적립) — 이 기간에 지급된 적립 포인트 합(주문·추천·수동, 지급 시점 기준). 판촉 비용/부채. */
    pointsGranted: z.number().int(),
    /** 포인트 판매 — 이 기간 매출 중 포인트로 전액결제된 금액. 매출(revenue)에 정가로 포함돼 있다. */
    pointsRedeemed: z.number().int(),
    /** 일반 판매 = 매출 − 포인트 판매. 현금(카드·후불)으로 받는 몫. */
    generalSales: z.number().int(),
    /**
     * 순이익 = 마진 − 포인트 판매(2026-09-30 회의 결정). 포인트로 판 몫은 현금이 안 들어오므로 이익에서 뺀다.
     * 마진과 같은 기준(공급 스냅샷 있는 주문, marginOrders)으로 합산한다. 포인트 지급(pointsGranted)은
     * 빼지 않는다 — 지급과 판매를 둘 다 빼면 같은 포인트가 두 번 차감된다(지급은 참고 지표).
     */
    netProfit: z.number().int(),
  }),
  series: z.array(
    z.object({
      bucket: z.string(), // day: YYYY-MM-DD · week: 주 시작(월) 날짜 · month: YYYY-MM
      orders: z.number().int(),
      revenue: z.number().int(),
    }),
  ),
  byOccasion: z.array(
    z.object({ occasionType, orders: z.number().int(), revenue: z.number().int() }),
  ),
  topCompanies: z.array(
    z.object({
      companyId: z.number().int(),
      name: z.string(),
      orders: z.number().int(),
      revenue: z.number().int(),
    }),
  ),
})
export type SalesReportResponse = z.infer<typeof salesReportResponse>

// ── B. 정산 (시점 스냅샷) ────────────────────────────────

export const settlementReportResponse = z.object({
  /** 배송 완료됐는데 아직 청구 라인에 안 걸린 주문 — 청구 누락 감시 */
  unbilled: z.object({ orders: z.number().int(), amount: z.number().int() }),
  /** 청구서 상태별 파이프라인 */
  invoicesByStatus: z.array(
    z.object({ status: z.string(), count: z.number().int(), amount: z.number().int() }),
  ),
  /** 미수금 = 발행 이후(SENT·AWAITING_PAYMENT·PARTIALLY_PAID·OVERDUE) Σ(총액−납부액) */
  receivable: z.number().int(),
  payouts: z.object({
    plannedCount: z.number().int(),
    plannedAmount: z.number().int(),
    /** 이번 달(KST) 지급 완료 합 */
    paidThisMonth: z.number().int(),
    byVendor: z.array(
      z.object({
        vendorId: z.number().int(),
        name: z.string(),
        plannedCount: z.number().int(),
        plannedAmount: z.number().int(),
      }),
    ),
  }),
})
export type SettlementReportResponse = z.infer<typeof settlementReportResponse>

// ── C. 운영 ─────────────────────────────────────────────

export const opsReportQuery = reportRangeQuery
export type OpsReportQuery = z.infer<typeof opsReportQuery>

/** 리드타임(시간 단위, 소수 1자리). 표본 없으면 null. */
const leadTime = z.object({
  avgHours: z.number().nullable(),
  medianHours: z.number().nullable(),
  samples: z.number().int(),
})

export const opsReportResponse = z.object({
  leadTimes: z.object({
    /** 접수 → 발주(PO_SENT 최초 진입) */
    submitToPo: leadTime,
    /** 발주 → 업체 수락 */
    poToAccept: leadTime,
    /** 접수 → 배송 완료 */
    submitToDelivered: leadTime,
  }),
  events: z.object({
    poSent: z.number().int(),
    vendorRejected: z.number().int(),
    deliveryIssue: z.number().int(),
  }),
  /** 업체 귀속은 현재 배정(order_supply) 기준 근사 — 활성 업체 1곳인 현 단계 전제 */
  byVendor: z.array(
    z.object({
      vendorId: z.number().int(),
      name: z.string(),
      poSent: z.number().int(),
      accepted: z.number().int(),
      rejected: z.number().int(),
      delivered: z.number().int(),
    }),
  ),
})
export type OpsReportResponse = z.infer<typeof opsReportResponse>
